import * as React from "react";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

export interface FormInputProps
  extends React.ComponentProps<"input"> {
  label?: string;
  error?: string;
}

const Input = React.forwardRef<HTMLInputElement, FormInputProps>(
  (
    {
      className,
      type = "text",
      label,
      required,
      name,
      id,
      value,
      onChange,
      onBlur,
      onFocus,
      error,
      step = 1,
      ...props
    },
    ref,
  ) => {
    const inputId = id || name;
    const hasError = Boolean(error);
    const errorId = inputId ? `${inputId}-error` : undefined;

    const inputNode = (
      <input
        ref={ref}
        id={inputId}
        name={name}
        type={type}
        data-slot="input"
        step={type === "number" ? step : undefined}
        {...(value !== undefined ? { value } : {})}
        onFocus={(e) => {
          if (type === "number") {
            e.target.select();
          }
          onFocus?.(e);
        }}
        onChange={(e) => {
          if (
            type === "number" &&
            e.target.value.length > 1 &&
            /^0\d+/.test(e.target.value)
          ) {
            e.target.value = e.target.value.replace(/^0+(?=\d)/, "");
          }
          onChange?.(e);
        }}
        onBlur={onBlur}
        required={required}
        aria-invalid={hasError}
        aria-describedby={hasError ? errorId : undefined}
        className={cn(
          "file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground border-input flex h-9 w-full min-w-0 rounded-md border bg-white px-3 py-1 text-sm shadow-xs transition-colors outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50",
          hasError
            ? "border-red-400 focus:border-red-500 focus:ring-1 focus:ring-red-500"
            : "focus:border-brand-primary focus:ring-1 focus:ring-brand-primary",
          className,
        )}
        {...props}
      />
    );

    if (!label && !error) {
      return inputNode;
    }

    return (
      <div className="w-full space-y-1.5">
        {label ? (
          <div className="flex items-center gap-1">
            <Label
              htmlFor={inputId}
              className="text-sm font-medium text-gray-900"
            >
              {label}
            </Label>
            {required && <span className="text-red-500 font-semibold">*</span>}
          </div>
        ) : null}
        {inputNode}
        {hasError ? (
          <p id={errorId} className="text-xs text-red-600" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    );
  },
);
Input.displayName = "Input";

export default Input;
export { Input };
